import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  ExitToApp as ExitIcon,
  Edit as EditIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format, formatDistance } from 'date-fns';

// Types
interface Visit {
  id: number;
  check_in_time: string;
  check_out_time: string | null;
  badge_id: string;
  host_name: string;
  purpose: string;
  notes: string | null;
}

interface Visitor {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'checked-in' | 'checked-out' | 'pre-registered';
  photo_url: string | null;
  total_visits: number;
  current_visit: Visit | null;
  visits: Visit[];
}

// Tab panel interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`visitor-tabpanel-${index}`}
      aria-labelledby={`visitor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const VisitorDetails: React.FC = () => {
  // State
  const [visitor, setVisitor] = useState<Visitor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Hooks
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Fetch visitor data
  useEffect(() => {
    const fetchVisitor = async () => {
      try {
        if (!id) {
          setError('Visitor ID is required');
          setLoading(false);
          return;
        }
        
        const response = await axios.get(`/api/visitors/${id}`);
        setVisitor(response.data);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching visitor:', err);
        setError(err.response?.data?.message || 'Failed to load visitor information');
        setLoading(false);
      }
    };
    
    fetchVisitor();
  }, [id]);
  
  // For demo purposes - remove in production
  useEffect(() => {
    // Mock data for preview
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        const mockVisits: Visit[] = [
          {
            id: 1,
            check_in_time: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
            check_out_time: null,
            badge_id: 'V-2023-001',
            host_name: 'Michael Brown',
            purpose: 'Meeting',
            notes: null
          },
          {
            id: 2,
            check_in_time: new Date(Date.now() - 604800000).toISOString(), // 1 week ago
            check_out_time: new Date(Date.now() - 601200000).toISOString(), // 1 week ago + 1 hour
            badge_id: 'V-2023-002',
            host_name: 'Sarah Johnson',
            purpose: 'Interview',
            notes: 'Follow-up interview for marketing position'
          },
          {
            id: 3,
            check_in_time: new Date(Date.now() - 1209600000).toISOString(), // 2 weeks ago
            check_out_time: new Date(Date.now() - 1206000000).toISOString(), // 2 weeks ago + 1 hour
            badge_id: 'V-2023-003',
            host_name: 'David Wilson',
            purpose: 'Client Meeting',
            notes: 'Quarterly review meeting'
          }
        ];

        const mockVisitor: Visitor = {
          id: 1,
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '(555) 123-4567',
          company: 'Acme Corporation',
          status: 'checked-in',
          photo_url: null,
          total_visits: 3,
          current_visit: mockVisits[0],
          visits: mockVisits
        };
        
        setVisitor(mockVisitor);
        setLoading(false);
      }, 1000);
    }
  }, []);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle check out
  const handleCheckOut = () => {
    if (!visitor) return;
    navigate(`/check-out/${visitor.id}`);
  };
  
  // Handle print badge
  const handlePrintBadge = () => {
    if (!visitor || !visitor.current_visit) return;
    
    // In a real application, this would trigger badge printing
    // For demo purposes, we'll just open a new window with badge info
    const badgeWindow = window.open('', '_blank');
    
    if (badgeWindow) {
      badgeWindow.document.write(`
        <html>
          <head>
            <title>Visitor Badge</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
              }
              .badge {
                border: 1px solid #ccc;
                border-radius: 8px;
                padding: 20px;
                width: 300px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              }
              .badge-header {
                text-align: center;
                margin-bottom: 20px;
              }
              .badge-content {
                margin-bottom: 20px;
              }
              .badge-footer {
                text-align: center;
                font-size: 12px;
                color: #666;
              }
              .badge-id {
                font-size: 18px;
                font-weight: bold;
                text-align: center;
                margin: 10px 0;
                color: #1976d2;
              }
              .field {
                margin-bottom: 10px;
              }
              .label {
                font-weight: bold;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="badge-header">
                <h2>VISITOR</h2>
              </div>
              <div class="badge-id">${visitor.current_visit.badge_id}</div>
              <div class="badge-content">
                <div class="field">
                  <div class="label">Name:</div>
                  <div>${visitor.name}</div>
                </div>
                <div class="field">
                  <div class="label">Host:</div>
                  <div>${visitor.current_visit.host_name}</div>
                </div>
                <div class="field">
                  <div class="label">Purpose:</div>
                  <div>${visitor.current_visit.purpose}</div>
                </div>
                <div class="field">
                  <div class="label">Date:</div>
                  <div>${new Date(visitor.current_visit.check_in_time).toLocaleDateString()}</div>
                </div>
              </div>
              <div class="badge-footer">
                Please wear this badge visibly at all times while on the premises.
              </div>
            </div>
            <script>
              window.onload = function() {
                window.print();
              }
            </script>
          </body>
        </html>
      `);
      badgeWindow.document.close();
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error || !visitor) {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitor Details
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          <Alert severity="error">
            {error || 'Visitor not found'}
          </Alert>
          
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/visitors')}
            >
              Back to Visitors
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked-in':
        return 'success';
      case 'checked-out':
        return 'default';
      case 'pre-registered':
        return 'info';
      default:
        return 'default';
    }
  };
  
  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'checked-in':
        return 'Checked In';
      case 'checked-out':
        return 'Checked Out';
      case 'pre-registered':
        return 'Pre-Registered';
      default:
        return status;
    }
  };
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Visitor Details
          </Typography>
          
          <Box>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/visitors')}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/visitors/edit/${visitor.id}`)}
            >
              Edit
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          View and manage visitor information and visit history.
        </Typography>
      </Box>
      
      {/* Visitor header card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src={visitor.photo_url || undefined} 
                sx={{ 
                  width: 80, 
                  height: 80,
                  mr: 2,
                  bgcolor: theme.palette.primary.main
                }}
              >
                {!visitor.photo_url && <PersonIcon fontSize="large" />}
              </Avatar>
              
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {visitor.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label={getStatusLabel(visitor.status)} 
                    color={getStatusColor(visitor.status) as any} 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  
                  {visitor.company && (
                    <Typography variant="body2" color="text.secondary">
                      {visitor.company}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
              {visitor.status === 'checked-in' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintBadge}
                    sx={{ mr: 1 }}
                  >
                    Print Badge
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ExitIcon />}
                    onClick={handleCheckOut}
                  >
                    Check Out
                  </Button>
                </>
              )}
              
              {visitor.status === 'pre-registered' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/check-in?visitor=${visitor.id}`)}
                >
                  Check In
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabs */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="visitor tabs">
            <Tab label="Overview" id="visitor-tab-0" aria-controls="visitor-tabpanel-0" />
            <Tab label="Visit History" id="visitor-tab-1" aria-controls="visitor-tabpanel-1" />
          </Tabs>
        </Box>
        
        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Visitor Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Visitor Information
                  </Typography>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <List disablePadding>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PersonIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Name" 
                        secondary={visitor.name}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                    
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={visitor.email || 'Not provided'}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                    
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PhoneIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Phone" 
                        secondary={visitor.phone || 'Not provided'}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                    
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <BusinessIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Company" 
                        secondary={visitor.company || 'Not provided'}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                    
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <HistoryIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Total Visits" 
                        secondary={visitor.total_visits}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Current Visit Information */}
            <Grid item xs={12} md={6}>
              {visitor.current_visit ? (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Current Visit
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <List disablePadding>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AccessTimeIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Check-In Time" 
                          secondary={format(new Date(visitor.current_visit.check_in_time), 'PPpp')}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <EventIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Duration" 
                          secondary={
                            visitor.current_visit.check_out_time 
                              ? formatDistance(
                                  new Date(visitor.current_visit.check_in_time), 
                                  new Date(visitor.current_visit.check_out_time),
                                  { addSuffix: false }
                                )
                              : formatDistance(
                                  new Date(visitor.current_visit.check_in_time), 
                                  new Date(),
                                  { addSuffix: false }
                                ) + ' (ongoing)'
                          }
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PersonIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Host" 
                          secondary={visitor.current_visit.host_name}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <BusinessIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Purpose" 
                          secondary={visitor.current_visit.purpose}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body1' }}
                        />
                      </ListItem>
                      
                      {visitor.current_visit.notes && (
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <NoteIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Notes" 
                            secondary={visitor.current_visit.notes}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            secondaryTypographyProps={{ variant: 'body1' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      No Current Visit
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary" align="center">
                        {visitor.status === 'pre-registered' 
                          ? 'This visitor is pre-registered but has not checked in yet.'
                          : 'This visitor is not currently checked in.'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Visit History Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Visit History
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Complete record of all visits by {visitor.name}.
            </Typography>
            
            {visitor.visits.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No visit history found for this visitor.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Badge ID</TableCell>
                      <TableCell>Host</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Check-In</TableCell>
                      <TableCell>Check-Out</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visitor.visits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>
                          {format(new Date(visit.check_in_time), 'PP')}
                        </TableCell>
                        <TableCell>{visit.badge_id}</TableCell>
                        <TableCell>{visit.host_name}</TableCell>
                        <TableCell>{visit.purpose}</TableCell>
                        <TableCell>
                          {format(new Date(visit.check_in_time), 'p')}
                        </TableCell>
                        <TableCell>
                          {visit.check_out_time 
                            ? format(new Date(visit.check_out_time), 'p')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {visit.check_out_time 
                            ? formatDistance(
                                new Date(visit.check_in_time), 
                                new Date(visit.check_out_time),
                                { addSuffix: false }
                              )
                            : formatDistance(
                                new Date(visit.check_in_time), 
                                new Date(),
                                { addSuffix: false }
                              ) + ' (ongoing)'}
                        </TableCell>
                        <TableCell>
                          {visit.check_out_time 
                            ? <Chip 
                                size="small" 
                                label="Completed" 
                                color="default" 
                                icon={<CheckCircleIcon />} 
                              />
                            : <Chip 
                                size="small" 
                                label="Active" 
                                color="success" 
                                icon={<AccessTimeIcon />} 
                              />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default VisitorDetails;
